import { Schema, SchemaGetter } from "effect";
import { CommitId, TreeId } from "../development-identifiers.ts";
import { WorkerstationState } from "../models.ts";
import { HistoryExpectedUpdatedAt } from "./workspace.ts";

// History accepts reserved station slugs that the editor's StationId rejects.
// A distinct brand preserves that wire contract without claiming editor access.
export const HistoryStationId = /* @__PURE__ */ (() => Schema.String.check(
  Schema.isPattern(/^[a-z0-9][a-z0-9-]{0,63}$/)
).pipe(Schema.brand("history-station")))();
export type HistoryStationId = typeof HistoryStationId.Type;

export const HistoryStationIdFromText = /* @__PURE__ */ (() => Schema.String.pipe(
  Schema.decodeTo(HistoryStationId, {
    decode: SchemaGetter.transform(value => value.toLowerCase()),
    encode: SchemaGetter.transform(value => value)
  })
))();

export const HistoryTagName = /* @__PURE__ */ (() => Schema.String.check(
  Schema.isPattern(/^[A-Za-z0-9][A-Za-z0-9._-]{0,63}$/)
).pipe(Schema.brand("history-tag")))();
export type HistoryTagName = typeof HistoryTagName.Type;

// Creation ignores excess fields. Empty messages reach the native owner, which
// has different legacy/repository validation and persistence behavior.
export const StationTagCreateBody = /* @__PURE__ */ (() => Schema.Struct({
  name: HistoryTagName,
  message: Schema.optionalKey(Schema.String.check(Schema.isMaxLength(500)))
}))();
export type StationTagCreateBody = typeof StationTagCreateBody.Type;

/** The repository owner has a stricter message contract than legacy snapshots. */
export const HistoryRepositoryTagMessage = /* @__PURE__ */ (() => Schema.String.check(
  Schema.isMaxLength(500),
  Schema.makeFilter(value => value.length > 0 && value === value.trim() && !/[\x00-\x1f\x7f]/.test(value))
))();

const RestoreShape = /* @__PURE__ */ (() => Schema.Struct({
  expectedUpdatedAt: Schema.optionalKey(Schema.Unknown),
  expectedRevision: Schema.optionalKey(Schema.Unknown),
  expectedCommitId: Schema.optionalKey(Schema.Unknown)
}))();
const RestoreField = /* @__PURE__ */ (() => Schema.Literals([
  "expectedUpdatedAt", "expectedRevision", "expectedCommitId"
]))();
export const unknownStationTagRestoreField = (body: Readonly<Record<string, unknown>>) =>
  Object.keys(body).find(key => !Schema.is(RestoreField)(key));

// Preserve the old first-unknown-key rule, including its empty-key exception.
// Authority-specific revision checks happen after tag/ref lookup, in the route.
export const StationTagRestoreBody = /* @__PURE__ */ (() => Schema.StructWithRest(
  RestoreShape, [Schema.Record(Schema.String, Schema.Unknown)]
).check(Schema.makeFilter(body => !unknownStationTagRestoreField(body))).pipe(
  Schema.decodeTo(RestoreShape, {
    decode: SchemaGetter.transform(value => value),
    encode: SchemaGetter.transform(value => value)
  })
))();
export type StationTagRestoreBody = typeof StationTagRestoreBody.Type;

export const StationTagRestoreBodyFromText = /* @__PURE__ */ (() => Schema.String.pipe(
  Schema.decodeTo(Schema.fromJsonString(StationTagRestoreBody), {
    decode: SchemaGetter.transform(value => value.trim() ? value : "{}"),
    encode: SchemaGetter.transform(value => value)
  })
))();

const LegacyRestoreShape = /* @__PURE__ */ (() => Schema.Struct({
  expectedUpdatedAt: Schema.optionalKey(HistoryExpectedUpdatedAt)
}))();
export const LegacyStationTagRestoreBody = /* @__PURE__ */ (() => StationTagRestoreBody.pipe(
  Schema.decodeTo(LegacyRestoreShape)
))();

/** Native repository restore chooses numeric fields by type, then validates. */
export function stationTagExpectedRevision(body: StationTagRestoreBody, current: number): number {
  return typeof body.expectedRevision === "number" ? body.expectedRevision :
    typeof body.expectedUpdatedAt === "number" ? body.expectedUpdatedAt : current;
}

export const StationTagCompareQuery = /* @__PURE__ */ (() => {
  const text = Schema.optionalKey(Schema.Union([Schema.String, Schema.Array(Schema.String)]));
  return Schema.Struct({ before: text, after: text });
})();

export const HistoryRepositoryRefName = /* @__PURE__ */ (() => Schema.String.check(
  Schema.isPattern(/^[A-Za-z0-9][A-Za-z0-9._/-]{0,127}$/),
  Schema.makeFilter(value => !value.includes("//") && !value.split("/").some(part => part === "." || part === ".."))
))();
export const HistoryRepositoryCommitText = /* @__PURE__ */ (() => Schema.String.check(
  Schema.isPattern(/^sha256:[a-f0-9]{64}$/)
).pipe(Schema.decodeTo(CommitId)))();

export const StationTag = /* @__PURE__ */ (() => Schema.Struct({
  id: Schema.String, name: Schema.String, message: Schema.optionalKey(Schema.String),
  createdAt: Schema.Number,
  workspaceCount: Schema.Int.check(Schema.isGreaterThanOrEqualTo(0)),
  fileCount: Schema.Int.check(Schema.isGreaterThanOrEqualTo(0))
}))();
export type StationTag = typeof StationTag.Type;

export const StationTagManifest = /* @__PURE__ */ (() => Schema.Struct({
  ...StationTag.fields, station: WorkerstationState, workspaceIds: Schema.Array(Schema.String)
}))();
export type StationTagManifest = typeof StationTagManifest.Type;

export const HistoryRepositoryRef = /* @__PURE__ */ (() => Schema.Struct({
  name: Schema.String, commitId: CommitId, revision: Schema.Number, kind: Schema.String,
  frozen: Schema.Boolean, createdAt: Schema.Number, updatedAt: Schema.Number
}))();
export type HistoryRepositoryRef = typeof HistoryRepositoryRef.Type;

export const HistoryRepositoryTagResult = /* @__PURE__ */ (() => Schema.Struct({
  tag: HistoryRepositoryRef, commitId: CommitId, treeId: TreeId, message: Schema.optionalKey(Schema.String)
}))();
export const HistoryRepositoryRestoreResult = /* @__PURE__ */ (() => Schema.Union([
  Schema.Struct({ ok: Schema.Literal(true), ref: HistoryRepositoryRef, commitId: CommitId, treeId: TreeId }),
  Schema.Struct({ ok: Schema.Literal(false), observedRevision: Schema.Number, observedCommitId: Schema.NullOr(CommitId) })
]))();
export type HistoryRepositoryRestoreResult = typeof HistoryRepositoryRestoreResult.Type;

export const HistoryRepositoryDiff = /* @__PURE__ */ (() => Schema.Struct({
  beforeCommitId: CommitId, afterCommitId: CommitId,
  beforeTreeId: Schema.optionalKey(TreeId), afterTreeId: Schema.optionalKey(TreeId),
  files: Schema.Array(Schema.Struct({
    kind: Schema.Literals(["added", "modified", "deleted", "renamed"]), workspaceId: Schema.String,
    path: Schema.String, previousPath: Schema.optionalKey(Schema.String),
    beforeBlobId: Schema.optionalKey(Schema.String), afterBlobId: Schema.optionalKey(Schema.String)
  })),
  topology: Schema.Array(Schema.Struct({
    kind: Schema.Literals(["workspace-added", "workspace-deleted", "workspace-order-changed", "active-workspace-changed"]),
    workspaceId: Schema.optionalKey(Schema.String),
    before: Schema.optionalKey(Schema.Union([Schema.String, Schema.Array(Schema.String)])),
    after: Schema.optionalKey(Schema.Union([Schema.String, Schema.Array(Schema.String)]))
  })),
  configuration: Schema.Array(Schema.Struct({
    workspaceId: Schema.String, field: Schema.Literals(["folders", "openTabs", "currentFile", "options"]),
    before: Schema.optional(Schema.Json), after: Schema.optional(Schema.Json)
  })),
  stationConfiguration: Schema.Array(Schema.Struct({
    field: Schema.Literals(["schemaVersion", "bindings", "migrations", "configuration"]),
    before: Schema.optional(Schema.Json), after: Schema.optional(Schema.Json)
  }))
}))();
export type HistoryRepositoryDiff = typeof HistoryRepositoryDiff.Type;

const LegacyTag = /* @__PURE__ */ (() => Schema.Struct({ ...StationTag.fields, source: Schema.Literal("legacy") }))();
const RepositoryTag = /* @__PURE__ */ (() => Schema.Struct({
  id: Schema.String, name: Schema.String, createdAt: Schema.Number,
  commitId: CommitId, revision: Schema.Number, source: Schema.Literal("repository")
}))();

export const StationTagListResponse = /* @__PURE__ */ (() => Schema.Struct({
  stationId: HistoryStationId,
  tags: Schema.Array(Schema.Union([LegacyTag, Schema.Struct({
    ...RepositoryTag.fields, kind: Schema.String, updatedAt: Schema.Number
  })])),
  sources: Schema.optionalKey(Schema.Struct({ legacy: Schema.Int, repository: Schema.Int }))
}))();

export const StationTagReadResponse = /* @__PURE__ */ (() => Schema.Struct({
  stationId: HistoryStationId,
  tag: Schema.Union([
    Schema.Struct({ ...StationTagManifest.fields, source: Schema.Literal("legacy") }),
    Schema.Struct({ ...RepositoryTag.fields, kind: Schema.String })
  ])
}))();

export const StationTagCreateResponse = /* @__PURE__ */ (() => Schema.Struct({
  stationId: HistoryStationId,
  tag: Schema.Union([LegacyTag, Schema.Struct({
    ...RepositoryTag.fields, treeId: TreeId, message: Schema.optionalKey(Schema.String)
  })])
}))();

export const StationTagDeleteResponse = /* @__PURE__ */ (() => Schema.Struct({
  ok: Schema.Literal(true), stationId: HistoryStationId, name: HistoryTagName
}))();

export const StationTagRestoreResponse = /* @__PURE__ */ (() => Schema.Union([
  Schema.Struct({ ok: Schema.Literal(true), stationId: HistoryStationId, restoredTag: HistoryTagName,
    backupTag: StationTag, source: Schema.Literal("legacy") }),
  Schema.Struct({ ok: Schema.Literal(true), stationId: HistoryStationId, restoredTag: HistoryTagName,
    source: Schema.Literal("repository"), commitId: CommitId, treeId: TreeId,
    revision: Schema.Number, runtimeMutated: Schema.Literal(false) })
]))();

export const StationTagCompareResponse = /* @__PURE__ */ (() => Schema.Struct({
  stationId: HistoryStationId, ...HistoryRepositoryDiff.fields
}))();
